<script setup>
import { PhxIcon } from '@phx-styleguides/iwc';
import { computed, nextTick, ref, watch } from 'vue';
import { clientApi } from '@phoenix/clientApi';
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import FormGroup from '@maison/components/form/FormGroup.vue';
import TextField from '@maison/components/form/fields/TextField.vue';
import { useI18n } from 'vue-i18n';
import Panel from '@maison/components/common/Panel.vue';
import { showPanel } from '@phoenix/helpers/panel-helper';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  searchText: {
    type: String,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    required: true,
  },
  findYourNumberCTAText: {
    type: String,
    required: true,
  },
  findYourNumberPanelTitle: {
    type: String,
    required: true,
  },
  findYourNumberPanelItems: {
    type: Array,
    required: true,
  },
  notMyWatchCTAText: {
    type: String,
    required: true,
  },
  findYourStrapTitle: {
    type: String,
    required: true,
  },
  findYourStrapText: {
    type: String,
    required: true,
  },
  findYourStrapDiscoverCTAText: {
    type: String,
    required: true,
  },
  findYourStrapReplaceCTAText: {
    type: String,
    required: true,
  },
});

const currentProduct = ref(null);
const searchValue = ref(null);
const searchInputRef = ref(null);

const searchError = ref(null);

const findYourNumberPanelId = 'find-your-number-panel';

const { t } = useI18n();

const configuratorLink = computed(() => {
  return `${window.route('strap_finder_configurator_page')}?reference=${currentProduct.value?.modelReference}`;
});

async function onSearch() {
  if (!searchValue.value) {
    return;
  }

  try {
    const requestResult = await clientApi.get(
      window.route('api.strapFinder.getWatchFromReference', { reference: searchValue.value }),
    );

    const data = requestResult?.data?.data;
    if (!data) {
      throw 'No data found in request result';
    }

    currentProduct.value = data;
  } catch (error) {
    searchError.value = t('StrapFinderSearch.searchError', { reference: searchValue.value });
  }
}

function onReset() {
  currentProduct.value = null;
  searchValue.value = null;

  nextTick(() => {
    searchInputRef.value?.$el.focus();
  });
}

function openPanel() {
  showPanel(findYourNumberPanelId);
}

watch(
  () => searchValue.value,
  (newValue) => {
    searchError.value = null;
  },
);
</script>

<template>
  <section v-if="!currentProduct" class="section section--spacing-5 box box--border-block-end">
    <div class="center center--spacing-4 center--sizing-3xl strap-finder-search">
      <div class="strap-finder-search__intro">
        <h1 class="heading-small strap-finder-search__title">{{ title }}</h1>
        <div class="stack stack--spacing-2">
          <p>
            {{ searchText }}
          </p>
          <button
            type="button"
            class="btn btn--underline btn--sm btn--fade"
            data-panel-open="find-your-number-panel"
            @click="openPanel"
          >
            <span class="btn__text">{{ findYourNumberCTAText }}</span>
          </button>
        </div>
      </div>

      <form class="form-control stack stack--spacing-3 strap-finder-search__input" @submit.prevent="onSearch">
        <form-group :attrs="{ id: 'search', name: 'search', errors: searchError }" :show-label="false">
          <text-field ref="searchInputRef" v-model="searchValue" :placeholder="searchPlaceholder" />
          <template #icon>
            <div class="input-adornment input-adornment--end">
              <phx-icon icon="arrow-right" bidirectional />
            </div>
          </template>
        </form-group>
      </form>
    </div>
  </section>

  <div v-else class="section section--spacing-4">
    <div class="strap-finder stack stack--spacing-4">
      <div class="strap-finder-sections">
        <div class="strap-finder-media-wrapper strap-finder-media-wrapper--static">
          <div class="strap-finder-media">
            <div class="strap-finder-media__item strap-finder-media__item--card box box--solid theme-grey-95">
              <div class="picture">
                <picture-lazy v-bind="currentProduct.image" />
              </div>
              <div class="strap-finder-media__caption">
                <div class="stack stack--spacing-1">
                  <div class="heading-small">{{ currentProduct.name }}</div>
                  <ul>
                    <li>
                      {{ $t('StrapFinderSearch.product.serialNumber', { reference: currentProduct.modelReference }) }}
                    </li>
                    <li>
                      {{
                        $t('StrapFinderSearch.product.defaultStrapDescription', {
                          description: currentProduct.defaultStrapDescription,
                        })
                      }}
                    </li>
                    <li>
                      {{ $t('StrapFinderSearch.product.defaultStrapSize', { size: currentProduct.defaultStrapSize }) }}
                    </li>
                  </ul>

                  <button type="button" class="btn btn--underline btn--fade" @click="onReset">
                    <span class="btn__text">{{ notMyWatchCTAText }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="strap-finder-options stack stack--spacing-6">
          <div class="strap-finder__options-head stack stack--spacing-4">
            <h3 class="heading-medium">{{ findYourStrapTitle }}</h3>
            <div class="rich-text">
              {{ findYourStrapText }}
            </div>
          </div>

          <div class="strap-finder__options-action">
            <a :href="configuratorLink" class="btn btn--primary btn--plain">
              <span class="btn__text">{{ findYourStrapDiscoverCTAText }}</span>
            </a>

            <a :href="`${configuratorLink}&replace=true`" class="btn btn--secondary btn--plain">
              <span class="btn__text">{{ findYourStrapReplaceCTAText }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <teleport to="#teleport-target-panels">
    <panel
      :id="findYourNumberPanelId"
      :classes="['dialog']"
      :title="findYourNumberPanelTitle"
      open-from="end"
      boxed-content
    >
      <template #content>
        <template v-for="(panelItem, index) in findYourNumberPanelItems" :key="panelItem.id">
          <div class="stack stack--spacing-3 panel--open-from-end">
            <h2 class="heading-small">{{ panelItem.title }}</h2>
            <p>{{ panelItem.text }}</p>
            <div v-if="panelItem.image" class="picture">
              <picture-lazy v-bind="panelItem.image" />
            </div>
          </div>
          <hr v-if="index !== findYourNumberPanelItems.length - 1" class="divider divider--spacing-6" />
        </template>
      </template>
    </panel>
  </teleport>
</template>
